/**
 * Anonymisiert personenbezogene Daten (PII) aus deutschsprachigen Transkripten.
 * Entfernt Namen, Adressen, Telefonnummern, E-Mails, Versicherungsnummern etc.
 * Medizinisch relevante Informationen bleiben erhalten.
 */

// Patterns for name detection after common German introductions
const NAME_INTRO_PATTERNS = [
  // "Mein Name ist Max Müller", "Ich heiße Anna Schmidt"
  /(mein name ist|ich hei[sß]e|ich bin der|ich bin die)\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+){0,2})/gi,
  // "Herr Müller", "Frau Schmidt", "Dr. Meier", "Professor Weber"
  /(herr|frau|dr\.|doktor|professor|prof\.)\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/gi,
  // "Patient Schmidt", "Patientin Müller"
  /(patient|patientin)\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/gi,
];

// Phone number patterns (German)
const PHONE_PATTERNS = [
  // International: +49 xxx xxx, 0049 xxx xxx
  /(\+49|0049)[\s\-/]?\(?\d{2,4}\)?[\s\-/]?\d{3,8}/g,
  // Domestic: 0xxx/xxxxxx, 0xxx-xxxxxx, 0xxx xxxxxx
  /\b0\d{2,4}[\s\-/]\d{3,8}\b/g,
  // Mobile: 01xx xxxxxxx
  /\b01[5-7]\d[\s\-/]?\d{3,4}[\s\-/]?\d{3,4}\b/g,
];

// Address patterns
const ADDRESS_PATTERNS = [
  // Street + house number: "Hauptstraße 5", "Berliner Str. 12a"
  /[A-ZÄÖÜ][a-zäöüß]+(stra[sß]e|str\.|weg|gasse|platz|allee|ring|damm|ufer)\s*\d+\s*[a-z]?\b/gi,
  // "in der Hauptstraße" (without number)
  /(wohne|lebe|wohnhaft)\s+(in\s+der?\s+)?[A-ZÄÖÜ][a-zäöüß]+(stra[sß]e|str\.|weg|gasse|platz|allee)/gi,
  // PLZ + City: "80331 München"
  /\b\d{5}\s+[A-ZÄÖÜ][a-zäöüß]+\b/g,
];

// Date of birth patterns
const DOB_PATTERNS = [
  // "geboren am 15.03.1985", "Geburtsdatum 15.3.85"
  /(geboren\s*(am)?|geburtsdatum|geb\.)\s*:?\s*\d{1,2}[.\-/]\d{1,2}[.\-/]\d{2,4}/gi,
  // "vom 15. März 1985" after birth context
  /(geboren|geburtsdatum|geb\.)\s*:?\s*(am\s+)?\d{1,2}\.\s*(januar|februar|märz|april|mai|juni|juli|august|september|oktober|november|dezember)\s+\d{2,4}/gi,
];

// Email pattern
const EMAIL_PATTERN = /[\w.+\-]+@[\w.\-]+\.\w{2,}/g;

// Insurance number patterns (German Krankenversicherungsnummer)
const INSURANCE_PATTERNS = [
  // Format: Letter + 9 digits (e.g., A123456789)
  /\b[A-Z]\d{9}\b/g,
  // "Versicherungsnummer" followed by alphanumeric
  /(versicherungsnummer|versichertennummer|kvnr)\s*:?\s*[A-Z0-9]{8,12}/gi,
];

// Social security number (Sozialversicherungsnummer: 12 digits)
const SOCIAL_SECURITY_PATTERN = /\b\d{2}\s?\d{6}\s?[A-Z]\s?\d{3}\b/g;

export function anonymizeTranscript(text) {
  if (!text) return text;

  let result = text;

  // 1. Names (replace the captured name group, keep the intro)
  for (const pattern of NAME_INTRO_PATTERNS) {
    result = result.replace(pattern, (match, intro) => `${intro} [NAME]`);
  }

  // 2. Phone numbers
  for (const pattern of PHONE_PATTERNS) {
    result = result.replace(pattern, '[TELEFON]');
  }

  // 3. Addresses
  result = result.replace(ADDRESS_PATTERNS[0], '[ADRESSE]');
  result = result.replace(ADDRESS_PATTERNS[1], (match, verb) => `${verb} [ADRESSE]`);
  result = result.replace(ADDRESS_PATTERNS[2], '[ORT]');

  // 4. Date of birth (keep context word, replace date)
  for (const pattern of DOB_PATTERNS) {
    result = result.replace(pattern, (match, intro) => `${intro} [GEBURTSDATUM]`);
  }

  // 5. Email
  result = result.replace(EMAIL_PATTERN, '[EMAIL]');

  // 6. Insurance numbers
  for (const pattern of INSURANCE_PATTERNS) {
    result = result.replace(pattern, '[VERSICHERUNGSNR]');
  }

  // 7. Social security number
  result = result.replace(SOCIAL_SECURITY_PATTERN, '[SOZIALVERSNR]');

  return result;
}
